import { mybusinessbusinessinformation_v1 } from 'googleapis'
import { RawStoreData } from './stores/store-types'
import { DateTime } from 'luxon'
import { z } from 'zod'

export interface StoreHours {
	start: DateTime | null
	end: DateTime | null
}

export const AdpCsvRecordRegularHoursSchema = z.object({
	'Location Name': z.string().optional(),
	LocationName: z.string().optional(),
	Sunday_Open: z.string(),
	Sunday_Close: z.string(),
	Monday_Open: z.string(),
	Monday_Close: z.string(),
	Tuesday_Open: z.string(),
	Tuesday_Close: z.string(),
	Wednesday_Open: z.string(),
	Wednesday_Close: z.string(),
	Thursday_Open: z.string(),
	Thursday_Close: z.string(),
	Friday_Open: z.string(),
	Friday_Close: z.string(),
	Saturday_Open: z.string(),
	Saturday_Close: z.string(),
	'Effective Date': z.string(),
})

export type AdpCsvRecordRegularHours = z.infer<typeof AdpCsvRecordRegularHoursSchema>

export const AdpCsvRecordSpecialHoursSchema = z.object({
	'Location Name': z.string().optional(),
	LocationName: z.string().optional(),
	StartDate: z.string(),
	EndDate: z.string(),
	OpenTime: z.string(),
	ClosedTime: z.string(),
	'Closed(T/F)': z.string(),
	Reason: z.string(),
})

export type AdpCsvRecordSpecialHours = z.infer<typeof AdpCsvRecordSpecialHoursSchema>

export const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

/**
 * Translate the store hours to a format that can be used by the DateTime library
 * @param storeHours - The store hours to translate
 * @returns The translated store hours
 */
export function translateStoreHours(storeHours: string): StoreHours {
	const [open, close] = storeHours
		.toString()
		.split('-')
		.map((time) => time.trim())

	// Handle cases where am/pm is connected to minutes (e.g. "9:00am" -> "9:00 am")
	const formattedOpen = open.replace(/(?<!\s)([ap]m)$/i, ' $1') || ''
	const formattedClose = close?.replace(/(?<!\s)([ap]m)$/i, ' $1') || ''
	if (
		formattedOpen.toLowerCase() === 'closed' ||
		formattedClose.toLowerCase() === 'closed' ||
		formattedOpen === '' ||
		formattedClose === ''
	) {
		return {
			start: null,
			end: null,
		}
	}

	const now = DateTime.now()

	const start = parseTimeString(formattedOpen)?.set({
		year: now.year,
		month: now.month,
		day: now.day,
	})
	const end = parseTimeString(formattedClose)?.set({
		year: now.year,
		month: now.month,
		day: now.day,
	})

	if (!start || !end || !start.isValid || !end.isValid) {
		return {
			start: null,
			end: null,
		}
	}

	return {
		start,
		end,
	}
}

/**
 * Get the hours for a given day
 * @param store - The store to get the hours for
 * @param day - The day to get the hours for
 * @returns The hours for the given day
 */
export function getHoursForDay(store: RawStoreData, day: string) {
	const storeHours = store.hours[day.toLowerCase() as keyof typeof store.hours] as string
	return storeHours ? translateStoreHours(storeHours) : null
}

/**
 * Get the hours for the current day of the week
 * @param store - The store to get the hours for
 * @returns The hours for the current day of th week
 */
export function getTodaysHours(store: RawStoreData, localeTime: Date) {
	// Check if all days are null, which means the store has no hours set
	if (hasHours(store)) {
		const now = new Date()
		const day = now.toLocaleDateString('en-US', { weekday: 'long' })
		const storeHours = store.hours[day.toLowerCase() as keyof typeof store.hours] as string

		if (storeHours) {
			const { start, end } = translateStoreHours(storeHours)

			if (!start || !end) {
				return {
					day,
					start: null,
					end: null,
					isOpen: false,
					hoursNotAvailable: false,
				}
			}

			const currentTime = DateTime.fromJSDate(localeTime)
			return {
				day,
				start: start.toFormat('h:mm a'),
				end: end.toFormat('h:mm a'),
				isOpen: currentTime > start && currentTime < end,
				hoursNotAvailable: false,
			}
		}
	}

	return {
		day: null,
		start: null,
		end: null,
		isOpen: false,
		hoursNotAvailable: true,
	}
}

/**
 * Check if the store has hours set
 * @param store - The store to check
 * @returns True if the store has hours set, false otherwise
 */
export function hasHours(store: RawStoreData) {
	return Object.values(store.hours).some((hours) => hours !== null)
}

export interface FormHours {
	open: string | null
	close: string | null
	closed: boolean
}

export interface FormSpecialHours extends FormHours {
	date: Date
	name: string
}

/**
 * Convert regular hours from a storage format to a form format
 *
 * @param store - The store to convert the hours for
 * @returns Record<string, FormHours> - Object mapping each day to its hours in form format:
 *         - open: 24h time string (e.g. "09:00")
 *         - close: 24h time string (e.g. "17:00")
 *         - closed: boolean indicating if store is closed that day
 */
export function convertRegularHoursFromStorageFormatToFormFormat(store: RawStoreData) {
	return daysOfWeek.reduce(
		(acc, day) => {
			const lowerCaseDay = day.toLowerCase()
			const parsedHours = getHoursForDay(store, lowerCaseDay)
			acc[lowerCaseDay] = {
				open: parsedHours?.start?.toFormat('HH:mm') || '09:00',
				close: parsedHours?.end?.toFormat('HH:mm') || '17:00',
				closed: !parsedHours,
			}
			return acc
		},
		{} as Record<string, FormHours>,
	)
}

/**
 * Convert special hours from a storage format to a form format
 * @param special - The special hours in storage format (comma-separated string like "2024-01-01: 9:00 AM-5:00 PM,2024-01-02: Closed-Closed")
 * @returns Array of special hours in form format:
 *         - date: Date object
 *         - name: string (event name)
 *         - open: 24h time string (e.g. "09:00")
 *         - close: 24h time string (e.g. "17:00")
 *         - closed: boolean indicating if store is closed that day
 */
export function convertSpecialHoursFromStorageFormatToFormFormat(specialHours: string | null | undefined) {
	if (!specialHours) return []

	const specialHoursMap: Record<string, FormSpecialHours> = {}

	const hoursList = specialHours.split(',')
	for (const hours of hoursList) {
		const [date, timeRange] = hours.split(': ')
		const { start, end } = translateStoreHours(timeRange)
		let closed = false
		let openTime: FormHours['open'] = null
		let closeTime: FormHours['close'] = null

		if (!start || !end) {
			closed = true
		} else {
			// convert 12 hour time to 24 hour time
			openTime = start.toFormat('HH:mm')
			closeTime = end.toFormat('HH:mm')
		}

		specialHoursMap[date] = {
			date: new Date(date),
			name: '',
			open: openTime,
			close: closeTime,
			closed,
		}
	}

	return Object.values(specialHoursMap)
}

/**
 * Convert hours from a form format (Record<string, FormHours> with open/close times in 24h format "HH:mm")
 * to a storage format (Record<string, string> with times in "HH:mm-HH:mm" or "Closed")
 * @param regularHours - The regular hours in form format (e.g. { monday: { open: "09:00", close: "17:00", closed: false } })
 * @returns The hours in storage format (e.g. { monday: "09:00-17:00" } or { monday: "Closed" })
 */
export function convertRegularHoursFromFormFormatToStorageFormat(regularHours: Record<string, FormHours>) {
	return Object.entries(regularHours).reduce(
		(acc, [day, hours]) => {
			// convert the open and close times to 12-hour format
			const openTime = hours.open ? DateTime.fromFormat(hours.open, 'HH:mm').toFormat('h:mm a') : null
			const closeTime = hours.close ? DateTime.fromFormat(hours.close, 'HH:mm').toFormat('h:mm a') : null

			if (hours.closed) {
				acc[day] = 'Closed'
			} else {
				acc[day] = `${openTime || 'Closed'}-${closeTime || 'Closed'}`
			}
			return acc
		},
		{} as Record<string, string>,
	)
}

/**
 * Convert special hours from a form format to a string format used by the storage medium
 * @param specialHours - The special hours to convert
 * @returns The converted special hours
 */
export function convertSpecialHoursFromFormFormatToStorageFormat(specialHours: FormSpecialHours[]) {
	return specialHours
		.map((hours) => {
			if (hours.closed || !hours.open || !hours.close) {
				return `${DateTime.fromJSDate(hours.date).toFormat('MM/dd/yyyy')}: Closed-Closed`
			} else {
				const openTime = parseTimeString(hours.open)?.toFormat('h:mm a')
				const closeTime = parseTimeString(hours.close)?.toFormat('h:mm a')

				if (!openTime || !closeTime) {
					return `${DateTime.fromJSDate(hours.date).toFormat('MM/dd/yyyy')}: Closed-Closed`
				}

				return `${DateTime.fromJSDate(hours.date).toFormat('MM/dd/yyyy')}: ${openTime}-${closeTime}`
			}
		})
		.join(',')
}

export function convertGoogleSpecialHoursPeriodToFormFormat(
	period: mybusinessbusinessinformation_v1.Schema$SpecialHourPeriod,
) {
	if (!period.startDate) {
		return null
	}

	if (!period.startDate) {
		return null
	}
	const now = DateTime.now()
	const date = DateTime.fromObject({
		year: period.startDate.year || now.year,
		month: period.startDate.month || now.month,
		day: period.startDate.day || now.day,
	})

	if (period.closed) {
		return `${date.toFormat('MM/dd/yyyy')}: x`
	}

	if (!period.openTime || !period.closeTime || !period.openTime.hours || !period.closeTime.hours) {
		return null
	}
	const startTime = DateTime.fromObject({
		hour: period.openTime.hours,
		minute: period.openTime.minutes || 0,
	})
	const endTime = DateTime.fromObject({
		hour: period.closeTime.hours,
		minute: period.closeTime.minutes || 0,
	})

	return {
		date: date.toFormat('MM/dd/yyyy'),
		open: startTime.toFormat('h:mm a'),
		close: endTime.toFormat('h:mm a'),
		closed: period.closed,
	}
}

export function upcomingSpecialHoursMessage(locale: string, currentDate: Date, store: RawStoreData) {
	const specialHours = convertSpecialHoursFromStorageFormatToFormFormat(store.specialHours)

	if (specialHours.length === 0) return null

	const upcomingSpecialHours = specialHours.filter((hours) => {
		return DateTime.fromJSDate(hours.date).diff(DateTime.fromJSDate(currentDate), 'days').days > 0
	})

	if (upcomingSpecialHours.length === 0) return null
	// Convert a 24 time string to a locale time string
	return upcomingSpecialHours.map((hours) => {
		const dateString = hours.date.toLocaleDateString(locale)
		const localeOpenTime = hours.open
			? DateTime.fromFormat(hours.open, 'HH:mm').toLocaleString(DateTime.TIME_SIMPLE, { locale })
			: 'Closed'
		const localeCloseTime = hours.close
			? DateTime.fromFormat(hours.close, 'HH:mm').toLocaleString(DateTime.TIME_SIMPLE, { locale })
			: 'Closed'

		if (hours.closed) {
			return `${dateString} - Closed`
		} else {
			return `${dateString} - ${localeOpenTime} - ${localeCloseTime}`
		}
	})
}

/**
 * Converts a time range from "h:mm a" format (e.g. "9:00 AM - 5:00 PM") to 24-hour "HH:mm" format
 * required by Google My Business (e.g. "09:00 - 17:00").
 * @param timeRange - The time range string in "h:mm a - h:mm a" format (e.g. "9:00 AM - 5:00 PM")
 * @returns The time range in "HH:mm - HH:mm" format (e.g. "09:00 - 17:00"), or empty string if invalid input
 */
export function convertTimeRangeToGoogleFormat(timeRange: string) {
	const [open, close] = timeRange.split('-').map((time) => time.trim())
	if (!open || !close) return ''
	const openDate = DateTime.fromFormat(open, 'h:mm a')
	const closeDate = DateTime.fromFormat(close, 'h:mm a')
	return `${openDate.toFormat('HH:mm')} - ${closeDate.toFormat('HH:mm')}`
}

export function convertSpecialHoursToGoogleFormat(specialHours: string | null | undefined) {
	if (!specialHours) return ''
	const specialHoursConverted = convertSpecialHoursFromStorageFormatToFormFormat(specialHours)
	return specialHoursConverted
		.map((hours) => {
			if (hours.closed) {
				// Google My Business uses "x" to indicate a closed day
				return `${DateTime.fromJSDate(hours.date).toFormat('yyyy-MM-dd')}: x`
			} else {
				// But otherwise it's "HH:mm - HH:mm"
				return `${DateTime.fromJSDate(hours.date).toFormat('yyyy-MM-dd')}: ${hours.open} - ${hours.close}`
			}
		})
		.join(', ')
}

/**
 * Parse a time string to a DateTime object
 * @param time - The time string to parse
 * @returns The parsed DateTime object, or null if invalid input
 */
export function parseTimeString(time: string) {
	const formats = ['HH:mm', 'hh:mm a', 'h:mm a', 'H:mm']
	for (const format of formats) {
		const result = DateTime.fromFormat(time, format)
		if (result.isValid) {
			return result
		}
	}
	return null
}

export function parseDateString(date: string) {
	const formats = ['M/d/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd', 'yyyy-M-d', 'yyyy-MM-d', 'yyyy-M-dd', 'M/d/yy', 'MM/dd/yy']
	for (const format of formats) {
		const result = DateTime.fromFormat(date, format)
		if (result.isValid) {
			return result
		}
	}
	return null
}

/**
 * This takes records with the following format:
 *
 * Location Name,Sunday_Open,Sunday_Close,Monday_Open,Monday_Close,Tuesday_Open,Tuesday_Close,Wednesday_Open,Wednesday_Close,Thursday_Open,Thursday_Close,Friday_Open,Friday_Close,Saturday_Open,Saturday_Close,Effective Date
 * 115-Rehoboth,10:00,18:00,10:00,18:00,10:00,18:00,10:00,18:00,10:00,18:00,10:00,18:00,10:00,18:00,2025-04-12
 *
 * and updates the regular hours for the store.
 *
 * @param stores
 * @param records
 */
export function updateRegularHoursFromAdpRegularHoursFormat(
	stores: RawStoreData[],
	records: AdpCsvRecordRegularHours[],
) {
	records.forEach((record) => {
		// Extract store number from Location Name (e.g., "115-Rehoboth" -> "115")
		const locationName = record['Location Name'] || record['LocationName']
		if (!locationName) {
			throw new Error('Location Name is required')
		}
		const storeNumber = locationName.split('-')[0].trim()

		const storeIndex = stores.findIndex((store) => store.storeNumber === storeNumber)
		if (storeIndex === -1) {
			throw new Error(`Store ${storeNumber} not found`)
		}

		const store = stores[storeIndex]
		// Convert the hours to the format expected by the application
		const hours = {
			sunday: convertStartEndTimesToStorageFormat(record.Sunday_Open, record.Sunday_Close),
			monday: convertStartEndTimesToStorageFormat(record.Monday_Open, record.Monday_Close),
			tuesday: convertStartEndTimesToStorageFormat(record.Tuesday_Open, record.Tuesday_Close),
			wednesday: convertStartEndTimesToStorageFormat(record.Wednesday_Open, record.Wednesday_Close),
			thursday: convertStartEndTimesToStorageFormat(record.Thursday_Open, record.Thursday_Close),
			friday: convertStartEndTimesToStorageFormat(record.Friday_Open, record.Friday_Close),
			saturday: convertStartEndTimesToStorageFormat(record.Saturday_Open, record.Saturday_Close),
		}

		stores[storeIndex] = {
			...store,
			hours,
		}
	})
}

/**
 * This takes records with the following format:
 *
 * LocationName,StartDate,EndDate,OpenTime,ClosedTime,Closed(T/F),Reason
 * 102-Hagerstown Adult,04/20/2025,04/20/2025,,,T,Holy Weekend
 * 106-Jeffersonville,04/20/2025,04/20/2025,12:00,18:00,,Holy Weekend
 * 111-Pleasant Prairie,04/20/2025,04/20/2025,,,T,Holy Weekend
 *
 * and updates the special hours for the store.  Since the same store can be listed multiple times,
 * we start by preprocessing the list and generating a map of store numbers to records.
 *
 * @param stores
 * @param records
 */
export function updateSpecialHoursFromAdpSpecialHoursFormat(
	stores: RawStoreData[],
	records: AdpCsvRecordSpecialHours[],
) {
	const storeNumberToRecords = new Map<string, AdpCsvRecordSpecialHours[]>()
	records.forEach((record) => {
		const locationName = record['Location Name'] || record['LocationName']
		if (!locationName) {
			throw new Error('Location Name is required')
		}
		const storeNumber = locationName.split('-')[0].trim()
		if (!storeNumberToRecords.has(storeNumber)) {
			storeNumberToRecords.set(storeNumber, [])
		}
		storeNumberToRecords.get(storeNumber)?.push(record)
	})

	storeNumberToRecords.forEach((records, storeNumber) => {
		const storeIndex = stores.findIndex((store) => store.storeNumber === storeNumber)
		if (storeIndex === -1) {
			throw new Error(`Store ${storeNumber} not found`)
		}

		const store = stores[storeIndex]

		const newSpecialHours = records.map((record) => {
			const date = parseDateString(record['StartDate'])
			if (!date) {
				throw new Error('Invalid date format')
			}

			if (record['Closed(T/F)'] === 'T') {
				return {
					date: date.toJSDate(),
					open: null,
					close: null,
					closed: true,
					name: record['Reason'],
				}
			}

			const openTimeStr = record['OpenTime']?.trim()
			const closeTimeStr = record['ClosedTime']?.trim()

			if (!openTimeStr || !closeTimeStr) {
				throw new Error('Invalid time format - OpenTime and ClosedTime cannot be empty when store is not closed')
			}

			const open = parseTimeString(openTimeStr)
			const close = parseTimeString(closeTimeStr)
			if (!open || !close) {
				throw new Error('Invalid time format')
			}

			return {
				date: date.toJSDate(),
				open: open ? open.toFormat('HH:mm') : null,
				close: close ? close.toFormat('HH:mm') : null,
				closed: record['Closed(T/F)'] === 'T',
				name: record['Reason'],
			}
		})

		// MERGE with existing special hours instead of REPLACING
		// Fetch any previously uploaded special hours from the database
		const existingSpecialHours = store.specialHours
			? convertSpecialHoursFromStorageFormatToFormFormat(store.specialHours)
			: []

		// Combine existing hours with new hours from CSV
		const allSpecialHours = [...existingSpecialHours, ...newSpecialHours]

		// Filter out past dates (keep only future dates)
		const today = DateTime.now().startOf('day')
		const futureSpecialHours = allSpecialHours.filter((hour) => {
			const hourDate = DateTime.fromJSDate(hour.date).startOf('day')
			return hourDate >= today
		})

		// Deduplicate by date - if same date uploaded multiple times, keep the latest
		// Create a Map where key=date string and value=FormSpecialHours
		// This automatically overwrites older entries for the same date
		const hoursMap = new Map<string, FormSpecialHours>()
		futureSpecialHours.forEach((hour) => {
			const dateKey = DateTime.fromJSDate(hour.date).toFormat('MM/dd/yyyy')
			hoursMap.set(dateKey, hour)
		})

		// Convert Map back to array of unique hours
		const uniqueSpecialHours = Array.from(hoursMap.values())

		// Convert the merged array of FormSpecialHours into storage format (comma-separated string)
		const specialHoursString = convertSpecialHoursFromFormFormatToStorageFormat(uniqueSpecialHours)

		stores[storeIndex] = {
			...store,
			specialHours: specialHoursString, // preserves existing + adds new
		}
	})
}

export function convertStartEndTimesToStorageFormat(
	open: string | null | undefined,
	close: string | null | undefined,
): string | null {
	if (!open || !close) {
		return 'Closed - Closed'
	}
	const start = parseTimeString(open)
	const end = parseTimeString(close)
	if (!start || !end) {
		throw new Error('Invalid time format')
	}
	return `${start.toFormat('hh:mm a')} - ${end.toFormat('hh:mm a')}`
}
